/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './TimelineItem';

const TimelineItemManager: ComponentManagerFn<'TimelineItem'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'TimelineItem'>(platform, 'TimelineItem'),
    type: 'TimelineItem',
    name: 'Timeline Item',
    defaultChildren: ['TimelineSeparator', 'TimelineContent'],
    isChildAllowed: (page, item, childType, { appUid, pageId }) => {
      if (
        !(
          [
            'TriggerOnClick',
            'TimelineSeparator',
            'TimelineContent',
            'TimelineOppositeContent',
          ] as ItemType[]
        ).includes(childType)
      ) {
        return false;
      }
      const children = platform.getChildrenOfType(
        appUid,
        pageId,
        page,
        item,
        childType,
      );
      return children.length === 0 ? true : 'Already added';
    },

    onChildMoveIn: ({ page, value }) => {
      // Timeline Opposite content must be first child
      value.children = value.children.sort((a, b) => {
        const aItem = page[a];
        const bItem = page[b];
        if (aItem && bItem) {
          if (
            aItem.type === 'TimelineOppositeContent' ||
            bItem.type === 'TimelineContent'
          ) {
            return -1;
          }
          if (
            bItem.type === 'TimelineOppositeContent' ||
            aItem.type === 'TimelineContent'
          ) {
            return 1;
          }
        }
        return 0;
      });
    },
    defaultValue,
    props: {},
    getDisplayText: () => 'Timeline Item',
    category: 'Data',
    icon: { iconName: 'calendar' },
    description: `**Overview**<br/>
The Timeline Item is a structural container that represents a single event node within a Timeline. While the parent Timeline component defines the overall "track," the Timeline Item defines the specific "stop" on that track.
It acts as a wrapper that groups the visual marker (dot), the main content (text/card), and the optional opposite content (time/date) into a cohesive unit.

**1. Anatomy & Structure**<br/>
The Timeline Item is unique because it has no properties of its own. Instead, it relies entirely on a strict set of child components to define its appearance.
When you add a Timeline Item, it automatically creates the necessary children to form a standard layout:
* **Timeline Separator:** (Required) The visual axis. It contains the "Dot" (marker) and the "Connector" (line) that links this item to the next one.
* **Timeline Content:** (Required) The main body of the event. This is where you place the description, user avatar, or card details.
* **Timeline Opposite Content:** (Optional) A secondary area on the other side of the separator. Typically used for timestamps (e.g., "10:00 AM") or metadata.

**2. Automatic Layout Logic**<br/>
The component enforces specific layout rules to ensure the timeline renders correctly:
* **Child Limit:** You can have only one of each child type (Separator, Content, Opposite Content) per item.
* **Auto-Ordering:** The system automatically arranges these children. If you add "Opposite Content," it is automatically placed on the correct side (e.g., the left) regardless of the order you add it in the designer.

**3. Interactivity**<br/>
While the item itself is structural, you can make the entire row interactive.
* **TriggerOnClick:** Add this child component to execute a workflow when the user clicks anywhere on this specific timeline event. (Use Case: Click an "Order Shipped" event to open the shipment tracking page).

**4. Placement**<br/>
* **Parent Requirement:** This component must be a direct child of a Timeline. It cannot exist in isolation.`,
  };
};

export default TimelineItemManager;
